/**
 * tapTempo — the master clock, driven by the Space bar.
 *
 * Space is dual-purpose, disambiguated by how long it's held:
 *   • Quick tap  (< TAP_HOLD_THRESHOLD_MS)  → a tap-tempo hit.
 *   • Held longer                            → live-performance mode: while held,
 *                                              active hold-effects pulse to the beat.
 *
 * Like every other input piece, this writes only to the paramBus. It publishes
 * three slowly-changing values and never touches the renderer:
 *   tempo.bpm       derived beats-per-minute (0 until two taps exist)
 *   tempo.anchorMs  performance.now() timestamp of the most recent downbeat —
 *                   the phase reference. The render loop derives the continuous
 *                   beat phase from this + bpm on its own, so the bus isn't spammed.
 *   tempo.live      1 while Space is held past the threshold, else 0
 */

import { ParamBus } from '../state/paramBus';

/** Timer injection points (kept minimal so tests can stub them). */
export type ScheduleFn = (handler: () => void, ms: number) => unknown;
export type CancelFn = (id: unknown) => void;

/** Below this hold time a Space press is a tap; at/above it, it's live mode. */
export const TAP_HOLD_THRESHOLD_MS = 170;
/** Rolling tap buffer size. */
export const MAX_TAPS = 8;
/** A gap longer than this starts a fresh tempo rather than continuing. */
export const RESET_GAP_MS = 2000;

export class TapTempo {
  private taps: number[] = [];
  private bpmValue = 0;
  private anchorMs = 0;
  private liveMode = false;
  private spaceDownAt = 0;
  private holdTimer: unknown = null;

  constructor(
    private readonly bus: ParamBus,
    private readonly now: () => number = defaultNow,
    // Wrap the globals in arrows so they keep their `window` binding when
    // invoked as `this.setTimer(...)` — a bare reference throws "Illegal
    // invocation" in the browser.
    private readonly setTimer: ScheduleFn = (fn, ms) => setTimeout(fn, ms),
    private readonly clearTimer: CancelFn = (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  ) {}

  get bpm(): number {
    return this.bpmValue;
  }
  get tapCount(): number {
    return this.taps.length;
  }
  get isLive(): boolean {
    return this.liveMode;
  }

  /** Register a tap-tempo hit at time `t` (ms, performance.now clock). */
  tap(t: number): void {
    const last = this.taps[this.taps.length - 1];
    if (last !== undefined && t - last > RESET_GAP_MS) {
      this.taps = []; // gap too long — treat as a new tempo
    }
    this.taps.push(t);
    if (this.taps.length > MAX_TAPS) this.taps.shift();

    // Anchor phase to this tap (the strike is a downbeat).
    this.anchorMs = t;
    this.bus.set('tempo.anchorMs', this.anchorMs);

    if (this.taps.length >= 2) {
      let sum = 0;
      for (let i = 1; i < this.taps.length; i++) sum += this.taps[i] - this.taps[i - 1];
      const avgInterval = sum / (this.taps.length - 1);
      this.bpmValue = 60000 / avgInterval;
      this.bus.set('tempo.bpm', this.bpmValue);
    }
  }

  // ── Space-bar glue (from KeyState) ────────────────────────────────────
  spaceDown(): void {
    this.spaceDownAt = this.now();
    if (this.holdTimer) this.clearTimer(this.holdTimer);
    // If Space is still down after the threshold, enter live mode.
    this.holdTimer = this.setTimer(() => {
      this.holdTimer = null;
      this.setLive(true);
    }, TAP_HOLD_THRESHOLD_MS);
  }

  spaceUp(durationMs: number): void {
    if (this.holdTimer) {
      this.clearTimer(this.holdTimer);
      this.holdTimer = null;
    }
    if (this.liveMode) {
      this.setLive(false); // release ends live mode
    } else if (durationMs < TAP_HOLD_THRESHOLD_MS) {
      this.tap(this.spaceDownAt); // the beat was struck on key-down
    }
  }

  private setLive(on: boolean): void {
    if (this.liveMode === on) return;
    this.liveMode = on;
    this.bus.set('tempo.live', on ? 1 : 0);
  }
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
